import { Box, Button, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { useCurrentUser } from "../../../api/users";
import { useAuthContext } from "../../../contexts/auth";
import styles from "./editProfile.module.css";
import { useSnackbar } from "notistack";
import { ERROR_SNACKBAR, SUCCESS_SNACKBAR } from "../../../consts";

interface EditProfileProps {
  onSuccess?: () => void;
}

export const EditProfile = ({ onSuccess }: EditProfileProps) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser } = useAuthContext();

  const defaultFormData = { firstName: "", lastName: "" };
  const [formData, setFormData] = useState(defaultFormData);

  const { update, error } = useCurrentUser();

  useEffect(() => {
    if (currentUser) {
      setFormData({
        firstName: currentUser.first_name || "",
        lastName: currentUser.last_name || "",
      });
    }
  }, [currentUser]);

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleProfileEditSubmit = async () => {
    const { success } = await update({ first_name: formData.firstName, last_name: formData.lastName });
    if (success) {
      window.dispatchEvent(new Event("profileUpdated"));
      onSuccess?.();
      enqueueSnackbar(t("Profile updated successfully"), SUCCESS_SNACKBAR);
    } else {
      enqueueSnackbar(t("Profile update failed"), ERROR_SNACKBAR);
    }
  };

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
      <Box sx={{ flex: "1 1 45%", maxWidth: "calc(50% - 8px)" }}>
        <TextField
          name="firstName"
          label={t("First name")}
          size="small"
          fullWidth
          value={formData.firstName}
          onChange={handleFormChange}
          required
        />
        {error && error?.first_name && (
          <div className={styles.errorMessage}>{error.first_name[0]?.message}</div>
        )}
      </Box>
      <Box sx={{ flex: "1 1 45%", maxWidth: "calc(50% - 8px)" }}>
        <TextField
          name="lastName"
          label={t("Last name")}
          size="small"
          fullWidth
          value={formData.lastName}
          onChange={handleFormChange}
          required
        />
        {error && error?.last_name && (
          <div className={styles.errorMessage}>{error.last_name[0]?.message}</div>
        )}
      </Box>
      <Box sx={{ display: "flex", width: "100%", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          color="success"
          onClick={handleProfileEditSubmit}
        >
          {t("Update profile")}
        </Button>
      </Box>
    </Box>
  )
}
